/**
 * Elite Pulse API key: a short-lived credential a subscriber collects for themselves.
 *
 * The key is only obtainable from inside the platform by someone already signed in, so
 * holding one is itself evidence of a live subscription. It expires with the platform's
 * daily token reset, which is why the UI tells the holder when to come back rather than
 * presenting the key as something permanent.
 */

import { getApiUrl } from '../config/api';
import { getMmecAuthHeaderFields } from '../utils/authUtils';

export interface PulseApiKey {
    apiKey: string;
    expiresUtc: string;
    expiresInSeconds: number;
    scope: string;
}

/** Mint a key for the signed-in user. Throws with a readable message the modal can show. */
export async function fetchPulseApiKey(signal?: AbortSignal): Promise<PulseApiKey> {
    const response = await fetch(getApiUrl('/api/auth/pulse-api-key'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getMmecAuthHeaderFields() },
        signal,
    });

    if (response.status === 401) {
        throw new Error('Sign in again to collect an API key.');
    }
    if (!response.ok) {
        throw new Error(`Could not issue a key (HTTP ${response.status}).`);
    }

    const body = await response.json().catch(() => ({}));
    if (!body?.success || !body?.api_key) {
        throw new Error(body?.error || 'Could not issue a key.');
    }

    return {
        apiKey: String(body.api_key),
        expiresUtc: String(body.expires_utc || ''),
        expiresInSeconds: Number(body.expires_in_seconds || 0),
        scope: String(body.scope || 'elite_pulse'),
    };
}

/** "in about 24 hours" style text, so the holder knows when to collect the next one. */
export function describeExpiry(expiresUtc: string): string {
    if (!expiresUtc) return '';
    const expires = new Date(expiresUtc);
    if (Number.isNaN(expires.getTime())) return '';

    const hours = Math.round((expires.getTime() - Date.now()) / 3_600_000);
    if (hours <= 0) return 'Expired — refresh to collect a new one.';
    if (hours === 1) return 'Valid for about 1 hour.';
    return `Valid for about ${hours} hours, until ${expires.toLocaleString()}.`;
}
